/**
 * Precision CAP Portal — the single CAP a vendor can see.
 *
 * The backend is the authority on which CAP this user is bound to: GET
 * /api/portal/cap returns exactly that one CAP and its surrounding context.
 * There is no list, no switcher, no link out. The page has five plain
 * sections that mirror the internal CapDetail layout but are read-mostly:
 *
 *   What happened          — the deficiency
 *   Why it happened        — the why-chain and root cause
 *   What we're doing about it — milestones (read-only in Sprint C)
 *   Proof it worked        — evidence the user can post (note or link)
 *   Discussion             — comments anchored to the CAP itself
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PortalLayout from './PortalLayout';

const API = process.env.REACT_APP_API_URL || '';

const STATUS_TONES = {
  open:        'bg-amber-50 text-amber-800 ring-amber-200',
  in_progress: 'bg-blue-50 text-blue-800 ring-blue-200',
  validating:  'bg-violet-50 text-violet-800 ring-violet-200',
  approved:    'bg-emerald-50 text-emerald-800 ring-emerald-200',
  closed:      'bg-slate-100 text-slate-700 ring-slate-200',
  cancelled:   'bg-slate-100 text-slate-500 ring-slate-200',
};
const STATUS_LABELS = {
  open: 'Open',
  in_progress: 'In progress',
  validating: 'Validating',
  approved: 'Approved',
  closed: 'Closed',
  cancelled: 'Cancelled',
};

function StatusPill({ status }) {
  const tone = STATUS_TONES[status] || STATUS_TONES.open;
  const label = STATUS_LABELS[status] || status || 'Open';
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[12px] font-medium ring-1 ${tone}`}>
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// FindingConcierge — the owner-facing assistant. Grounded only on this one CAP
// (the server resolves it from the portal session). Explains the finding in
// plain language, why it matters, and what the owner needs to do.
// ---------------------------------------------------------------------------
function FindingConcierge() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const run = async () => {
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API}/api/portal/cap/assistant/explain`, { method: 'POST', credentials: 'include' });
      const d = await res.json();
      if (!res.ok || !d.success) throw new Error(d.message || 'The assistant is unavailable.');
      setResult(d.result);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };
  return (
    <section className="bg-gradient-to-br from-orange-50 to-white rounded-xl ring-1 ring-orange-200/70 p-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-[15px] font-semibold text-slate-900">Need help understanding this?</h2>
          <p className="text-[13px] text-slate-600 mt-0.5">Ask the assistant to explain this finding in plain language and what’s needed from you.</p>
        </div>
        <button type="button" onClick={run} disabled={loading}
          className="shrink-0 text-[13px] font-medium text-white bg-orange-600 hover:bg-orange-500 disabled:opacity-50 rounded-full px-4 py-2 transition">
          {loading ? 'Thinking…' : result ? 'Explain again' : 'Explain this'}
        </button>
      </div>
      {error && <p className="text-[13px] text-red-700 mt-3">{error}</p>}
      {result && (
        <div className="mt-4 space-y-3 text-sm text-slate-800">
          {result.explanation && <p className="leading-relaxed">{result.explanation}</p>}
          {result.why_it_matters && (
            <p className="leading-relaxed"><span className="font-semibold">Why it matters: </span>{result.why_it_matters}</p>
          )}
          {Array.isArray(result.what_you_need_to_do) && result.what_you_need_to_do.length > 0 && (
            <div>
              <p className="text-[12px] uppercase tracking-wide font-semibold text-slate-500 mb-1.5">What you need to do</p>
              <ul className="space-y-1.5">
                {result.what_you_need_to_do.map((s, i) => (
                  <li key={i} className="flex gap-2"><span className="text-orange-500 font-semibold">{i + 1}.</span><span>{s}</span></li>
                ))}
              </ul>
            </div>
          )}
          <p className="text-[11px] text-slate-400 pt-1">This is guidance to help you — your plan’s compliance team makes the final call.</p>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// EvidenceAssistant — per-milestone helper: draft the evidence note, or
// pre-check what the owner has written before they submit it.
// ---------------------------------------------------------------------------
function EvidenceAssistant({ milestoneId, note, setNote }) {
  const [busy, setBusy] = useState('');
  const [precheck, setPrecheck] = useState(null);
  const [docs, setDocs] = useState(null);
  const [error, setError] = useState('');
  const draft = async () => {
    setBusy('draft'); setError(''); setPrecheck(null);
    try {
      const res = await fetch(`${API}/api/portal/cap/assistant/draft-evidence`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ milestone_id: milestoneId }),
      });
      const d = await res.json();
      if (!res.ok || !d.success) throw new Error(d.message || 'Unavailable.');
      if (d.result.draft_note) setNote(d.result.draft_note);
      setDocs(d.result.suggested_documents || []);
    } catch (e) { setError(e.message); }
    finally { setBusy(''); }
  };
  const check = async () => {
    if (!note.trim()) { setError('Write your evidence first, then check it.'); return; }
    setBusy('check'); setError(''); setDocs(null);
    try {
      const res = await fetch(`${API}/api/portal/cap/assistant/precheck-evidence`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ milestone_id: milestoneId, text: note }),
      });
      const d = await res.json();
      if (!res.ok || !d.success) throw new Error(d.message || 'Unavailable.');
      setPrecheck(d.result);
    } catch (e) { setError(e.message); }
    finally { setBusy(''); }
  };
  const verdictTone = precheck?.verdict === 'looks_sufficient' ? 'text-emerald-700' : precheck?.verdict === 'needs_more' ? 'text-amber-700' : 'text-slate-600';
  return (
    <div className="mt-3 rounded-lg bg-orange-50/60 ring-1 ring-orange-200/60 p-3">
      <div className="flex items-center gap-2">
        <button type="button" onClick={draft} disabled={!!busy}
          className="text-[12px] font-medium text-orange-700 bg-white ring-1 ring-orange-200 rounded-md px-2.5 py-1 hover:bg-orange-50 disabled:opacity-50">
          {busy === 'draft' ? 'Drafting…' : 'Draft with assistant'}
        </button>
        <button type="button" onClick={check} disabled={!!busy}
          className="text-[12px] font-medium text-orange-700 bg-white ring-1 ring-orange-200 rounded-md px-2.5 py-1 hover:bg-orange-50 disabled:opacity-50">
          {busy === 'check' ? 'Checking…' : 'Check my evidence'}
        </button>
      </div>
      {error && <p className="text-[12px] text-red-700 mt-2">{error}</p>}
      {docs && docs.length > 0 && (
        <div className="mt-2 text-[12px] text-slate-600">
          <span className="font-semibold">Documents that would satisfy this: </span>{docs.join(', ')}
        </div>
      )}
      {precheck && (
        <div className="mt-2 text-[12px]">
          <span className={`font-semibold ${verdictTone}`}>
            {precheck.verdict === 'looks_sufficient' ? 'Looks sufficient' : precheck.verdict === 'needs_more' ? 'Needs a bit more' : 'Unclear'}
          </span>
          {precheck.assessment && <span className="text-slate-600"> — {precheck.assessment}</span>}
          {Array.isArray(precheck.missing) && precheck.missing.length > 0 && (
            <ul className="mt-1 space-y-0.5">
              {precheck.missing.map((m, i) => <li key={i} className="text-slate-600">• {m}</li>)}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function Section({ title, hint, children }) {
  return (
    <section className="bg-white rounded-xl ring-1 ring-slate-200 p-6">
      <header className="mb-4">
        <h2 className="text-[15px] font-semibold text-slate-900">{title}</h2>
        {hint && <p className="text-[13px] text-slate-500 mt-0.5">{hint}</p>}
      </header>
      {children}
    </section>
  );
}

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return '';
  }
}

function formatDateTime(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return '';
  }
}

export default function PortalCapView({ user, onSessionExpired }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Form state for evidence + comments
  const [activeMilestone, setActiveMilestone] = useState(null); // milestone id, or 'cap' for CAP-level
  const [evidenceKind, setEvidenceKind] = useState('note');
  const [evidenceNote, setEvidenceNote] = useState('');
  const [evidenceLink, setEvidenceLink] = useState('');
  const [evidenceDescription, setEvidenceDescription] = useState('');
  const [evidenceSubmitting, setEvidenceSubmitting] = useState(false);
  const [evidenceError, setEvidenceError] = useState('');

  const [commentBody, setCommentBody] = useState('');
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [commentError, setCommentError] = useState('');

  const loadCap = async () => {
    setError('');
    try {
      const res = await fetch(`${API}/api/portal/cap`, {
        method: 'GET',
        credentials: 'include',
      });
      if (res.status === 401) {
        if (onSessionExpired) onSessionExpired();
        else navigate('/login');
        return;
      }
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(body.message || 'We could not load your corrective action plan.');
        setData(null);
        return;
      }
      setData(body);
    } catch {
      setError('We could not reach the server. Please refresh in a moment.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitEvidence = async (e) => {
    e.preventDefault();
    setEvidenceError('');
    setEvidenceSubmitting(true);
    try {
      const payload = {
        kind: evidenceKind,
        description: evidenceDescription.trim() || undefined,
      };
      if (activeMilestone && activeMilestone !== 'cap') {
        payload.milestone_id = activeMilestone;
      }
      if (evidenceKind === 'note') {
        if (!evidenceNote.trim()) {
          setEvidenceError('Please add a short note describing what happened.');
          setEvidenceSubmitting(false);
          return;
        }
        payload.note_body = evidenceNote.trim();
      } else if (evidenceKind === 'link') {
        if (!evidenceLink.trim()) {
          setEvidenceError('Please paste the link you want to share.');
          setEvidenceSubmitting(false);
          return;
        }
        payload.link_url = evidenceLink.trim();
      }

      const res = await fetch(`${API}/api/portal/cap/evidence`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      if (res.status === 401) {
        if (onSessionExpired) onSessionExpired();
        else navigate('/login');
        return;
      }
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setEvidenceError(body.message || 'We could not save that evidence. Please try again.');
        return;
      }
      // Reset and reload
      setEvidenceNote('');
      setEvidenceLink('');
      setEvidenceDescription('');
      setActiveMilestone(null);
      await loadCap();
    } catch {
      setEvidenceError('We could not reach the server. Please try again.');
    } finally {
      setEvidenceSubmitting(false);
    }
  };

  const submitComment = async (e) => {
    e.preventDefault();
    setCommentError('');
    if (!commentBody.trim()) return;
    setCommentSubmitting(true);
    try {
      const res = await fetch(`${API}/api/portal/cap/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          anchor_type: 'cap',
          anchor_id: data?.cap?.id,
          body: commentBody.trim(),
        }),
      });
      if (res.status === 401) {
        if (onSessionExpired) onSessionExpired();
        else navigate('/login');
        return;
      }
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setCommentError(body.message || 'We could not post your comment. Please try again.');
        return;
      }
      setCommentBody('');
      await loadCap();
    } catch {
      setCommentError('We could not reach the server. Please try again.');
    } finally {
      setCommentSubmitting(false);
    }
  };

  if (loading) {
    return (
      <PortalLayout user={user}>
        <div className="text-sm text-slate-500">Loading your corrective action plan…</div>
      </PortalLayout>
    );
  }

  if (error) {
    return (
      <PortalLayout user={user}>
        <div className="max-w-2xl">
          <div className="rounded-lg bg-red-50 ring-1 ring-red-200 p-4 text-sm text-red-800">
            {error}
          </div>
          <button
            type="button"
            onClick={loadCap}
            className="mt-4 px-3 py-1.5 text-sm font-medium text-slate-700 bg-white ring-1 ring-slate-200 rounded-lg hover:bg-slate-50"
          >
            Try again
          </button>
        </div>
      </PortalLayout>
    );
  }

  if (!data?.cap) {
    return (
      <PortalLayout user={user}>
        <div className="text-sm text-slate-500">
          No corrective action plan is currently assigned to your account.
        </div>
      </PortalLayout>
    );
  }

  const {
    cap,
    whys = [],
    root_causes = [],
    milestones = [],
    evidence = [],
    comments = [],
  } = data;

  return (
    <PortalLayout user={user}>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-2">
          <StatusPill status={cap.status} />
          {cap.fdr_entity_name && (
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[12px] font-medium ring-1 bg-slate-100 text-slate-700 ring-slate-200">
              {cap.fdr_entity_name}
            </span>
          )}
        </div>
        <h1 className="text-[28px] font-semibold text-slate-900 leading-tight">
          {cap.title || 'Corrective Action Plan'}
        </h1>
        {cap.due_date && (
          <p className="text-[13px] text-slate-500 mt-1">Due {formatDate(cap.due_date)}</p>
        )}
        {user?.full_name && (
          <p className="text-[14px] text-slate-600 mt-3">
            Welcome, {user.full_name}. Everything below is the same plan your contact at
            Precision Compliance Group is looking at right now.
          </p>
        )}
      </div>

      <div className="space-y-4">
        {/* Owner concierge — plain-language help understanding the finding */}
        <FindingConcierge />

        {/* What happened */}
        <Section
          title="What happened"
          hint="The deficiency or finding, in plain language."
        >
          {cap.deficiency ? (
            <p className="text-sm text-slate-800 whitespace-pre-wrap">{cap.deficiency}</p>
          ) : (
            <p className="text-sm text-slate-400 italic">No description has been added yet.</p>
          )}
        </Section>

        {/* Why it happened */}
        <Section
          title="Why it happened"
          hint="The why-chain that traces back to the real root cause."
        >
          {whys.length === 0 ? (
            <p className="text-sm text-slate-400 italic">No why-chain has been written yet.</p>
          ) : (
            <ol className="space-y-2">
              {whys.map((w) => (
                <li key={w.id} className="flex gap-3">
                  <span className="text-[12px] font-mono text-slate-400 w-5 pt-0.5">{w.sequence}.</span>
                  <span className="text-sm text-slate-800">{w.text}</span>
                </li>
              ))}
            </ol>
          )}
          {root_causes.length > 0 && (
            <div className="mt-5 pt-4 border-t border-slate-100">
              <p className="text-[12px] uppercase tracking-wide font-semibold text-slate-500 mb-2">
                Root cause
              </p>
              <ul className="space-y-1.5">
                {root_causes.map((rc) => (
                  <li key={rc.id} className="text-sm text-slate-800">
                    <span className="font-medium">{rc.text}</span>
                    {rc.category && (
                      <span className="text-slate-400 text-[12px] ml-2">({rc.category})</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Section>

        {/* What we're doing about it */}
        <Section
          title="What we're doing about it"
          hint="Each step is owned by one person, with a deadline and a way to know it worked."
        >
          {milestones.length === 0 ? (
            <p className="text-sm text-slate-400 italic">No actions have been logged yet.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {milestones.map((m) => {
                const isOpen = activeMilestone === m.id;
                return (
                  <li key={m.id} className="py-3 first:pt-0 last:pb-0">
                    <div className="flex gap-3 items-start">
                      <span className="mt-0.5 inline-block w-2 h-2 rounded-full bg-slate-300 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-slate-800">{m.description}</p>
                        {m.measure_text && (
                          <p className="text-[12px] text-slate-500 mt-0.5">
                            How we'll know: {m.measure_text}
                          </p>
                        )}
                        <p className="text-[12px] text-slate-400 mt-0.5">
                          {m.target_date ? `Due ${formatDate(m.target_date)}` : 'No deadline yet'}
                          {' · '}{m.kind === 'preventive' ? 'Preventive' : 'Corrective'}
                          {' · '}{(m.status || 'open').replace('_', ' ')}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setActiveMilestone(isOpen ? null : m.id);
                          setEvidenceKind('note');
                          setEvidenceNote('');
                          setEvidenceLink('');
                          setEvidenceDescription('');
                          setEvidenceError('');
                        }}
                        className="text-[12px] font-medium text-slate-700 bg-white ring-1 ring-slate-200 rounded-md px-2.5 py-1 hover:bg-slate-50"
                      >
                        {isOpen ? 'Cancel' : 'Add evidence'}
                      </button>
                    </div>
                    {isOpen && (
                      <>
                        <EvidenceAssistant milestoneId={m.id} note={evidenceNote} setNote={setEvidenceNote} />
                        <EvidenceForm
                          kind={evidenceKind}
                          setKind={setEvidenceKind}
                          note={evidenceNote}
                          setNote={setEvidenceNote}
                          link={evidenceLink}
                          setLink={setEvidenceLink}
                          description={evidenceDescription}
                          setDescription={setEvidenceDescription}
                          submitting={evidenceSubmitting}
                          error={evidenceError}
                          onSubmit={submitEvidence}
                        />
                      </>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </Section>

        {/* Proof it worked */}
        <Section
          title="Proof it worked"
          hint="Evidence — notes or links — that show the action did what it was supposed to do."
        >
          {evidence.length === 0 ? (
            <p className="text-sm text-slate-400 italic">No evidence has been posted yet.</p>
          ) : (
            <ul className="space-y-3">
              {evidence.map((e) => (
                <li key={e.id} className="flex gap-3 items-start">
                  <span className="mt-0.5 inline-block w-2 h-2 rounded-full bg-emerald-300 shrink-0" />
                  <div className="flex-1 min-w-0">
                    {e.kind === 'note' && (
                      <p className="text-sm text-slate-800 whitespace-pre-wrap">{e.note_body}</p>
                    )}
                    {e.kind === 'link' && (
                      <a
                        href={e.link_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-sm text-slate-800 underline break-all"
                      >
                        {e.link_url}
                      </a>
                    )}
                    {e.kind === 'file' && (
                      <p className="text-sm text-slate-800">{e.file_name || 'File attachment'}</p>
                    )}
                    {e.description && (
                      <p className="text-[12px] text-slate-500 mt-0.5">{e.description}</p>
                    )}
                    <p className="text-[12px] text-slate-400 mt-0.5">
                      {e.created_by_name || 'Someone'} · {formatDateTime(e.created_at)}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}

          <div className="mt-5 pt-4 border-t border-slate-100">
            {activeMilestone === 'cap' ? (
              <EvidenceForm
                kind={evidenceKind}
                setKind={setEvidenceKind}
                note={evidenceNote}
                setNote={setEvidenceNote}
                link={evidenceLink}
                setLink={setEvidenceLink}
                description={evidenceDescription}
                setDescription={setEvidenceDescription}
                submitting={evidenceSubmitting}
                error={evidenceError}
                onSubmit={submitEvidence}
                onCancel={() => setActiveMilestone(null)}
              />
            ) : (
              <button
                type="button"
                onClick={() => {
                  setActiveMilestone('cap');
                  setEvidenceKind('note');
                  setEvidenceNote('');
                  setEvidenceLink('');
                  setEvidenceDescription('');
                  setEvidenceError('');
                }}
                className="text-sm font-medium text-slate-700 bg-white ring-1 ring-slate-200 rounded-lg px-3 py-1.5 hover:bg-slate-50"
              >
                Add evidence
              </button>
            )}
          </div>
        </Section>

        {/* Discussion */}
        <Section
          title="Discussion"
          hint="Anything you want to flag, ask, or clarify. Your contact at Precision Compliance Group can see every comment posted here."
        >
          {comments.length === 0 ? (
            <p className="text-sm text-slate-400 italic">No comments yet.</p>
          ) : (
            <ul className="space-y-4">
              {comments.map((c) => {
                // The portal query returns author_internal/author_external flags
                // (not raw names) so we never leak internal identities to the FDR.
                const who = c.author_name || (c.author_internal ? 'Compliance team' : c.author_external ? 'You' : 'Participant');
                return (
                <li key={c.id} className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-slate-200 text-slate-700 flex items-center justify-center text-[12px] font-semibold shrink-0">
                    {who.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] text-slate-700">
                      <span className="font-medium text-slate-900">{who}</span>
                      <span className="text-slate-400 ml-2">{formatDateTime(c.created_at)}</span>
                    </p>
                    <p className="text-sm text-slate-800 mt-0.5 whitespace-pre-wrap">{c.body}</p>
                  </div>
                </li>
                );
              })}
            </ul>
          )}

          <form onSubmit={submitComment} className="mt-5 pt-4 border-t border-slate-100 space-y-3">
            <textarea
              value={commentBody}
              onChange={(e) => setCommentBody(e.target.value)}
              placeholder="Write a short comment…"
              rows={3}
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
            />
            {commentError && (
              <div className="text-sm text-red-700 bg-red-50 ring-1 ring-red-200 rounded-md px-3 py-2">
                {commentError}
              </div>
            )}
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={commentSubmitting || !commentBody.trim()}
                className={`px-4 py-2 rounded-lg text-sm font-medium text-white transition ${
                  commentSubmitting || !commentBody.trim()
                    ? 'bg-slate-400 cursor-not-allowed'
                    : 'bg-slate-900 hover:bg-slate-800'
                }`}
              >
                {commentSubmitting ? 'Posting…' : 'Post comment'}
              </button>
            </div>
          </form>
        </Section>
      </div>
    </PortalLayout>
  );
}

function EvidenceForm({
  kind, setKind,
  note, setNote,
  link, setLink,
  description, setDescription,
  submitting, error,
  onSubmit, onCancel,
}) {
  return (
    <form onSubmit={onSubmit} className="bg-slate-50 ring-1 ring-slate-200 rounded-lg p-4 mt-3 space-y-3">
      <div className="flex gap-4 text-sm">
        <label className="inline-flex items-center gap-2 text-slate-700">
          <input
            type="radio"
            name="evidence-kind"
            value="note"
            checked={kind === 'note'}
            onChange={() => setKind('note')}
          />
          Note
        </label>
        <label className="inline-flex items-center gap-2 text-slate-700">
          <input
            type="radio"
            name="evidence-kind"
            value="link"
            checked={kind === 'link'}
            onChange={() => setKind('link')}
          />
          Link
        </label>
      </div>

      {kind === 'note' ? (
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="In a few sentences, describe what was done."
          rows={3}
          className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
        />
      ) : (
        <input
          type="url"
          value={link}
          onChange={(e) => setLink(e.target.value)}
          placeholder="https://..."
          className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
        />
      )}

      <input
        type="text"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="Optional one-line description"
        className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
      />

      {error && (
        <div className="text-sm text-red-700 bg-red-50 ring-1 ring-red-200 rounded-md px-3 py-2">
          {error}
        </div>
      )}

      <div className="flex justify-end gap-2">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 text-sm font-medium text-slate-700 bg-white ring-1 ring-slate-200 rounded-lg hover:bg-slate-50"
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={submitting}
          className={`px-3 py-1.5 text-sm font-medium text-white rounded-lg transition ${
            submitting ? 'bg-slate-400 cursor-not-allowed' : 'bg-slate-900 hover:bg-slate-800'
          }`}
        >
          {submitting ? 'Saving…' : 'Save evidence'}
        </button>
      </div>
    </form>
  );
}
